import React, { useState, useRef, useEffect, useCallback } from "react";
import { CircularProgress, Box, Typography, Fade, Avatar, ButtonBase } from "@mui/material";
import { styled, alpha, keyframes } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { Done, DoneAll } from "@mui/icons-material";
import ArchiveIcon from "@mui/icons-material/Archive";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ImageIcon from "@mui/icons-material/Image";
import TimerIcon from "@mui/icons-material/Timer";
import useApp from "../../../store/contexts/AppContext";
import capitalizeEachFirstWord from "../../../utils/CapLetters";
import TruncateString from "../../../utils/TruncateString";
import { CHAT_ROOMS, MESSAGE_FILE_TYPES } from "../../../config/index";
import { stripFormatting } from "../../../utils/parseMessageText";

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const now = new Date();
  let str = String(timestamp).trim().replace(' ', 'T');
  if (!str.endsWith('Z') && !str.includes('+')) str += 'Z';
  const date = new Date(str);
  const diff = now - date;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  if (days === 1) return "Yesterday";
  if (days < 7) return date.toLocaleDateString("en-US", { weekday: "long" });
  return date.toLocaleDateString("en-GB");
};

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.8; transform: scale(1.05); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ConversationsContainer = styled("div")({
  position: "relative",
  zIndex: 1,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
});

const ChatTitle = styled(Typography)(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 700,
  padding: "20px 16px 12px",
  color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.text.primary,
  letterSpacing: "-0.01em",
  flexShrink: 0,
}));

const ScrollableList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  minHeight: 0,
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.12)",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.24)" : "rgba(0,0,0,0.20)",
  },
}));

const ChatItem = styled(Box)(({ theme, isActive }) => ({
  cursor: "pointer",
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  background: isActive
    ? theme.palette.mode === "dark" ? alpha("#fff", 0.08) : alpha("#000", 0.05)
    : "transparent",
  transition: "background 0.2s ease",
  animation: `${slideIn} 0.3s ease-out`,
  "&:hover": {
    background: theme.palette.mode === "dark" ? alpha("#fff", 0.05) : alpha("#000", 0.04),
  },
  "&:active": {
    background: theme.palette.mode === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.08),
  },
}));

const AvatarWrapper = styled(Box)(({ isOnline, theme }) => ({
  position: "relative",
  flexShrink: 0,
  "&::after": isOnline ? {
    content: '""',
    position: "absolute",
    bottom: "2px",
    right: "2px",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    background: "#25D366",
    border: `2px solid ${theme.palette.mode === "dark" ? "#111" : "#fff"}`,
    animation: `${pulse} 2s ease-in-out infinite`,
  } : {},
}));

const DisappearingBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  width: 16,
  height: 16,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  border: `2px solid ${theme.palette.mode === "dark" ? "#111" : "#fff"}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .MuiSvgIcon-root": { fontSize: "9px", color: "#fff" },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 52,
  height: 52,
  fontSize: "20px",
  fontWeight: 700,
  background: theme.palette.mode === "dark" ? "#2a2a2a" : "#ffffff",
  color: theme.palette.mode === "dark" ? "#ccc" : "#555",
  border: `1.5px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.08) : alpha("#000", 0.1)}`,
}));

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "4px",
});

const ChatUsername = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.text.primary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  lineHeight: 1.3,
}));

const LastMessage = styled(Typography)(({ theme, hasUnread }) => ({
  fontSize: "14px",
  fontWeight: hasUnread ? 500 : 400,
  color: theme.palette.mode === "dark"
    ? hasUnread ? alpha(theme.palette.common.white, 0.75) : alpha(theme.palette.common.white, 0.55)
    : hasUnread ? alpha(theme.palette.text.primary, 0.7) : alpha(theme.palette.text.secondary, 0.6),
  display: "flex",
  alignItems: "center",
  gap: "4px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const RightSide = styled(Box)({
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  justifyContent: "center",
  gap: "6px",
  minWidth: "fit-content",
});

const Timestamp = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 400,
  color: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.45)
    : alpha(theme.palette.text.secondary, 0.5),
  whiteSpace: "nowrap",
}));

const UnreadBadge = styled(Box)({
  minWidth: "20px",
  height: "20px",
  borderRadius: "10px",
  background: "#25D366",
  color: "#000",
  fontSize: "12px",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 6px",
  boxShadow: "0 1px 3px rgba(37, 211, 102, 0.4)",
});

const CheckmarkIcon = styled(Box)(({ theme, isRead, isOnline }) => ({
  display: "flex",
  alignItems: "center",
  color: isRead
    ? "#53BDEB"
    : (theme.palette.mode === "dark" ? alpha("#fff", 0.55) : alpha("#000", 0.4)),
  fontSize: "16px",
  lineHeight: 1,
  "& .MuiSvgIcon-root": { fontSize: "16px" },
}));

const LoadingNotice = styled(Box)(({ theme }) => ({
  textAlign: "center",
  color: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.5) : alpha(theme.palette.text.secondary, 0.6),
  fontSize: "0.85rem",
  margin: "16px auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  padding: "16px",
}));

const EndNotice = styled(Box)(({ theme }) => ({
  textAlign: "center",
  color: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.35) : alpha(theme.palette.text.secondary, 0.4),
  fontSize: "0.8rem",
  margin: "20px auto",
  padding: "16px",
}));

// ── Archive row (WhatsApp-style entry point) ──
const ArchiveRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "14px",
  padding: "10px 16px",
  cursor: "pointer",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.06) : alpha("#000", 0.06)}`,
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#000", 0.03),
  },
}));

const ArchiveIconWrap = styled(Box)(({ theme }) => ({
  width: 42,
  height: 42,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: theme.palette.mode === "dark" ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.1),
  flexShrink: 0,
}));

// ── Archive view header ──
const ArchiveHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "14px 16px",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.06) : alpha("#000", 0.06)}`,
  cursor: "pointer",
  flexShrink: 0,
  animation: `${slideDown} 0.25s ease-out`,
  "&:hover": {
    background: theme.palette.mode === "dark" ? alpha("#fff", 0.03) : alpha("#000", 0.02),
  },
}));

const EmptyArchive = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  padding: "60px 20px",
  color: theme.palette.mode === "dark" ? alpha("#fff", 0.35) : alpha(theme.palette.text.secondary, 0.4),
}));

const FilterBar = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "0 16px 10px",
  overflowX: "auto",
  flexShrink: 0,
  "&::-webkit-scrollbar": { display: "none" },
});

const FilterChip = styled(ButtonBase)(({ theme, active }) => ({
  borderRadius: "20px",
  padding: "5px 16px",
  fontSize: "13px",
  fontWeight: active ? 600 : 400,
  whiteSpace: "nowrap",
  flexShrink: 0,
  transition: "background 0.18s, color 0.18s",
  background: active
    ? theme.palette.primary.main
    : theme.palette.mode === "dark" ? alpha("#fff", 0.08) : alpha("#000", 0.07),
  color: active
    ? "#fff"
    : theme.palette.mode === "dark" ? alpha("#fff", 0.75) : alpha("#000", 0.65),
  "&:hover": {
    background: active
      ? theme.palette.primary.dark
      : theme.palette.mode === "dark" ? alpha("#fff", 0.13) : alpha("#000", 0.11),
  },
}));

function ChatListItem({ currentChat, index, userData, activeChat, typingByRoom, onChatClick }) {
  const otherUserForDirectChat =
    currentChat?.type === CHAT_ROOMS.TYPES.SINGLE
      ? currentChat?.room_participants?.find(
          (p) => p.id?.toString() !== userData.id?.toString()
        ) || { username: currentChat?.name || "Unknown" }
      : { username: currentChat?.name || "Group Chat" };

  // Skip local-only entries that should never appear as the chat preview:
  //  • is_blocked_period — messages that couldn't be delivered (blocked)
  //  • is_optimistic / is_pending — still uploading / sending
  // System notices (tip, keep, disappearing toggle) DO appear here intentionally.
  const lastMessageOnChat = [...(currentChat.messages || [])]
    .reverse()
    .find(m => !m.is_blocked_period && !m.is_optimistic && !m.is_pending) || null;
  const isActive = activeChat?.id === currentChat.id;
  const isOnline = otherUserForDirectChat?.is_online === 1;
  const unreadCount = currentChat.unread_count || 0;
  const hasUnread = unreadCount > 0;
  const isSentByMe = lastMessageOnChat?.sender_id?.toString() === userData.id?.toString();
  const isRead = lastMessageOnChat?.is_read === 1;
  const firstLetter = otherUserForDirectChat?.username?.[0]?.toUpperCase() || "?";
  const timestamp = formatTimestamp(lastMessageOnChat?.created_at || currentChat.created_at);
  const isMuted = !!currentChat.is_muted;

  const isOtherTyping = Object.keys(typingByRoom?.[currentChat.id] || {}).length > 0;
  const isDisappearingOn = (currentChat.disappearing_interval || 0) > 0;

  const isLastMsgImage = !lastMessageOnChat?.is_deleted
    && lastMessageOnChat?.is_file
    && lastMessageOnChat?.file_type === MESSAGE_FILE_TYPES.IMAGE;

  const lastMessageText = lastMessageOnChat?.is_deleted
    ? "This message was deleted"
    : isLastMsgImage
      ? null
      : lastMessageOnChat
        ? TruncateString(stripFormatting(lastMessageOnChat.message), 32)
        : "Tap to start chatting";

  return (
    <Fade in timeout={250 + index * 25} key={currentChat.id}>
      <ChatItem isActive={isActive} onClick={() => onChatClick(currentChat)}>
        <AvatarWrapper isOnline={isOnline}>
          <StyledAvatar src={otherUserForDirectChat?.profile_image_url || otherUserForDirectChat?.profile_pic || otherUserForDirectChat?.avatar}>
            {firstLetter}
          </StyledAvatar>
          {isDisappearingOn && (
            <DisappearingBadge title="Disappearing messages on">
              <TimerIcon />
            </DisappearingBadge>
          )}
        </AvatarWrapper>

        <ContentArea>
          <ChatUsername>{capitalizeEachFirstWord(otherUserForDirectChat?.username)}</ChatUsername>
          <LastMessage hasUnread={hasUnread}>
            {isOtherTyping ? (
              <span style={{ fontStyle: "italic", color: "#25D366", fontSize: "12px" }}>typing...</span>
            ) : (
            <>
            {isSentByMe && lastMessageOnChat && !lastMessageOnChat.is_deleted && (
              <CheckmarkIcon isRead={isRead} isOnline={isOnline}>
                {isRead || isOnline ? <DoneAll /> : <Done />}
              </CheckmarkIcon>
            )}
            {isLastMsgImage ? (
              <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                <ImageIcon sx={{ fontSize: "13px", opacity: 0.7, flexShrink: 0 }} />
                Image
              </span>
            ) : (
              <span style={{ fontStyle: lastMessageOnChat?.is_deleted ? "italic" : "normal", opacity: lastMessageOnChat?.is_deleted ? 0.7 : 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                {lastMessageText}
              </span>
            )}
            </>
            )}
          </LastMessage>
        </ContentArea>

        <RightSide>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {isMuted && (
              <VolumeOffIcon sx={{ fontSize: "14px", opacity: 0.45 }} />
            )}
            <Timestamp>{timestamp}</Timestamp>
          </Box>
          {hasUnread && (
            <UnreadBadge>{unreadCount > 99 ? "99+" : unreadCount}</UnreadBadge>
          )}
        </RightSide>
      </ChatItem>
    </Fade>
  );
}

export default function Conversations() {
  const history = useHistory();
  const {
    appState: { userData, chats, activeChat },
    switchActiveChat,
    fetchMoreRoomsAndMessagesApiCall,
    typingByRoom,
  } = useApp();

  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedLastConversation, setReachedLastConversation] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const chatConversationsContainerRef = useRef(null);

  const activeChats = chats.filter((c) => c && !c.is_archived);
  const archivedChats = chats.filter((c) => c && c.is_archived);

  const handleChatClick = useCallback((chat) => {
    switchActiveChat(chat);
    if (chat.type === CHAT_ROOMS.TYPES.SINGLE) {
      const other = chat.room_participants?.find(
        (p) => p.id?.toString() !== userData.id?.toString()
      );
      if (other?.username) history.push(`/messages/${other.username}`);
    } else {
      history.push("/messages");
    }
  }, [switchActiveChat, history, userData.id]);

  const handleLoadMoreConversations = useCallback(async () => {
    if (loadingMore || reachedLastConversation) return;
    setLoadingMore(true);
    const result = await fetchMoreRoomsAndMessagesApiCall();
    setLoadingMore(false);
    if (result.reachedLastConversationAfterloadingMore) {
      setReachedLastConversation(result.message);
    }
  }, [fetchMoreRoomsAndMessagesApiCall, loadingMore, reachedLastConversation]);

  useEffect(() => {
    const el = chatConversationsContainerRef.current;
    const handleScroll = () => {
      if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
        handleLoadMoreConversations();
      }
    };
    if (el) {
      el.addEventListener("scroll", handleScroll);
      return () => el.removeEventListener("scroll", handleScroll);
    }
  }, [handleLoadMoreConversations]);

  // Auto-switch to archived view when navigating to an archived chat via URL.
  // Only depends on activeChat.id so clicking the back button (setShowArchived(false))
  // does NOT re-trigger this and send the user straight back to archived.
  useEffect(() => {
    if (activeChat?.id && activeChat.is_archived) {
      setShowArchived(true);
    }
  }, [activeChat?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredActiveChats = activeChats.filter((c) => {
    if (activeFilter === "unread") return (c.unread_count || 0) > 0;
    return true;
  });
  const displayList = showArchived ? archivedChats : filteredActiveChats;

  return (
    <ConversationsContainer>
      {showArchived ? (
        <ArchiveHeader onClick={() => setShowArchived(false)}>
          <ArrowBackIcon sx={{ fontSize: "20px", opacity: 0.7 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>
            Archived
            {archivedChats.length > 0 && (
              <Box component="span" sx={{ ml: 1, fontSize: "13px", fontWeight: 500, opacity: 0.65 }}>
                ({archivedChats.length})
              </Box>
            )}
          </Typography>
        </ArchiveHeader>
      ) : (
        <>
          <ChatTitle>
            Chats
            {activeChats.length > 0 && (
              <Box component="span" sx={{ ml: 1, fontSize: "13px", fontWeight: 500, opacity: 0.55 }}>
                ({activeChats.length})
              </Box>
            )}
          </ChatTitle>
          <FilterBar>
            {[
              { key: "all", label: "All" },
              { key: "unread", label: "Unread" },
            ].map(({ key, label }) => (
              <FilterChip key={key} active={activeFilter === key ? 1 : 0} onClick={() => setActiveFilter(key)}>
                {label}
              </FilterChip>
            ))}
          </FilterBar>
        </>
      )}

      <ScrollableList ref={chatConversationsContainerRef}>
        {/* Archive entry row (only in main view when archived chats exist) */}
        {!showArchived && archivedChats.length > 0 && (
          <Fade in timeout={300}>
            <ArchiveRow onClick={() => setShowArchived(true)}>
              <ArchiveIconWrap>
                <ArchiveIcon sx={{ fontSize: "20px", color: "primary.main" }} />
              </ArchiveIconWrap>
              <ContentArea>
                <ChatUsername>Archived</ChatUsername>
                <LastMessage hasUnread={false}>
                  {archivedChats.length} conversation{archivedChats.length !== 1 ? "s" : ""}
                </LastMessage>
              </ContentArea>
            </ArchiveRow>
          </Fade>
        )}

        {/* Empty archive state */}
        {showArchived && archivedChats.length === 0 && (
          <EmptyArchive>
            <ArchiveIcon sx={{ fontSize: "48px", opacity: 0.3 }} />
            <Typography sx={{ fontSize: "15px" }}>No archived chats</Typography>
          </EmptyArchive>
        )}

        {displayList.map((currentChat, index) => {
          if (!currentChat) return null;
          return (
            <ChatListItem
              key={currentChat.id}
              currentChat={currentChat}
              index={index}
              userData={userData}
              activeChat={activeChat}
              typingByRoom={typingByRoom}
              onChatClick={handleChatClick}
            />
          );
        })}

        {loadingMore && (
          <Fade in>
            <LoadingNotice>
              <CircularProgress size={18} thickness={4} />
              Loading more...
            </LoadingNotice>
          </Fade>
        )}

        {reachedLastConversation && !loadingMore && !showArchived && (
          <Fade in>
            <EndNotice>{reachedLastConversation}</EndNotice>
          </Fade>
        )}
      </ScrollableList>
    </ConversationsContainer>
  );
}
